import type {
  ColumnFiltersState,
  PaginationState,
  SortingState,
} from '@tanstack/react-table'
import {
  type DocumentTableRow,
  type SensorSearchItem,
  useRecentItems,
  useSensorSearch,
} from 'features/knowledgeDocumentViewer'
import { useDocChatAttachments } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocChatAttachmentsContext'
import { useDocumentBrowseFacets } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentBrowseFacets'
import { useEnabledDocumentSearch } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useEnabledDocumentSearch'
import { useSearchUserEditHydration } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useSearchUserEditHydration'
import {
  type FacetFilterId,
  facetOptionsToColumnValues,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFacets'
import { collectConversationDocumentIds } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/collectConversationDocumentIds'
import { collectDocChatContextDocuments } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatContextDocuments'
import {
  type DocumentSearchResult,
  type DrawingRevisionGroup,
  type Highlight,
  type PageClassification,
  type SearchEngine,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import { isAiDocumentSearchEngine } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchMode'
import { transformSystemDocumentToTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/helpers'
import { buildCitationDocumentLookup } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/resolveCitationDocument'
import {
  orderDocumentsById,
  orderDocumentsByScoreDesc,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/sortDocumentSearchResults'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useOffsetPagedSystemDocuments } from 'features/knowledgeDocumentViewer/hooks/useOffsetPagedSystemDocuments'
import { useSystemDocumentsByBqIds } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import type {
  DocumentSearchMode,
  SearchHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { getSystemDocumentBqId } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  unscopePageIdFromPipelineConfig,
  unscopeSearchDocumentIdForHydration,
} from 'features/knowledgeDocumentViewer/utils/pipelineConfigScopedIds'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, { createContext, useCallback, useContext, useMemo } from 'react'
import { DocumentsViewerContentFilter } from 'routes/documents/-searchParams'
import { KnowledgeDocumentEntry, RolodexEntryType } from 'types'

export type BrowseAllDisplayState = {
  displayedPageIndex: number
  displayedSorting: SortingState
  facetColumnValues: Partial<Record<FacetFilterId, string[]>>
  loadFullFilteredSet: boolean
}

interface DocumentSearchDataContextType {
  browseAllDisplay: BrowseAllDisplayState | undefined
  documentResults: DocumentTableRow[]
  documentTotalCount: number
  isLoadingDocuments: boolean
  documentsLoadError: Error | undefined
  sensorResults: SensorSearchItem[]
  sensorTotalCount: number
  isLoadingSensors: boolean
  recentItems: ReturnType<typeof useRecentItems>['recentItems']
  addRecentItem: ReturnType<typeof useRecentItems>['addRecentItem']
  elasticsearchHighlightsMap: Map<string, Highlight[]>
  pageClassificationsMap: Map<string, PageClassification[]>
  drawingRevisionGroupsMap: Map<string, DrawingRevisionGroup[]>
  documentRowsById: Map<string, DocumentTableRow>
  citationDocumentLookup: Map<string, DocumentTableRow>
  rawSearchData: DocumentSearchResult | undefined
  searchEngine: SearchEngine
  searchLatencyMs: number | undefined
  searchedSystemDocuments: SystemDocumentEntry[] | undefined
  refreshDocumentSearch: () => void
}

interface DocumentSearchDataProviderProps {
  children: React.ReactNode
  siteId: string
  tabId?: string
  searchQuery: string
  contentFilter: DocumentsViewerContentFilter
  searchMode: DocumentSearchMode
  linkedDocIds?: string[]
  unitId?: string
  browseAll?: boolean
  columnFilters?: ColumnFiltersState
  pagination?: PaginationState
  sorting?: SortingState
  docChat?: SearchHistoryItem['searchState']['docChat']
}

const DocumentSearchDataContext = createContext<
  DocumentSearchDataContextType | undefined
>(undefined)

const isLinkedDocsMode = (linkedDocIds?: string[], unitId?: string) =>
  (linkedDocIds && linkedDocIds.length > 0) || !!unitId

function mapSearchValuesToDocumentRowIds<T>(
  searchedSystemDocuments: SystemDocumentEntry[] | undefined,
  valuesBySearchId: Map<string, T>
): Map<string, T> {
  const map = new Map<string, T>()
  for (const entry of searchedSystemDocuments ?? []) {
    const bqId = getSystemDocumentBqId(entry)
    const value =
      (bqId !== undefined ? valuesBySearchId.get(bqId) : undefined) ??
      valuesBySearchId.get(entry.entry.id)
    if (value) {
      map.set(bqId ?? entry.entry.id, value)
    }
  }
  return map
}

export const DocumentSearchDataProvider: React.FC<
  DocumentSearchDataProviderProps
> = ({
  children,
  siteId,
  tabId,
  searchQuery,
  contentFilter,
  searchMode,
  linkedDocIds,
  unitId,
  browseAll = false,
  columnFilters,
  pagination,
  sorting,
  docChat,
}) => {
  const { activeTabId, canViewUserEdits, openTabs } =
    useKnowledgeDocumentUXContext()
  const {
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    selectedPipelineConfigId,
  } = usePipelineConfig()
  const linkedMode = isLinkedDocsMode(linkedDocIds, unitId)
  const shouldHydratePageUserEdits =
    canViewUserEdits && !linkedMode && !browseAll
  const shouldHydrateDocumentUserEdits = canViewUserEdits && !linkedMode
  const browsePageIndex = pagination?.pageIndex ?? 0
  const browsePageSize = pagination?.pageSize ?? 200
  const browseScopedTags = useMemo(() => {
    if (!browseAll || isPipelineConfigScopeLoading) return undefined
    return getPipelineConfigScopedTags({ site: siteId })
  }, [
    browseAll,
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    siteId,
  ])
  const {
    displayedPageIndex: browseDisplayedPageIndex,
    displayedSorting: browseDisplayedSorting,
    pageEntries: browsePageEntries,
    totalCount: browseTotalCount,
    isLoading: isLoadingBrowsePage,
    loadFullFilteredSet: browseLoadFullFilteredSet,
    error: browsePageError,
    refresh: refreshBrowsePage,
  } = useOffsetPagedSystemDocuments({
    columnFilters,
    enabled: browseAll,
    pageIndex: browsePageIndex,
    pageSize: browsePageSize,
    scopedTags: browseScopedTags,
    sorting,
  })
  const { optionsByFilterId: browseFacetOptions } = useDocumentBrowseFacets(
    browseScopedTags,
    columnFilters
  )
  const browseFacetColumnValues = useMemo(
    () => facetOptionsToColumnValues(browseFacetOptions),
    [browseFacetOptions]
  )
  const { sentAttachmentsById } = useDocChatAttachments()
  const sentAttachments = useMemo(
    () => Array.from(sentAttachmentsById.values()),
    [sentAttachmentsById]
  )
  const docChatContextDocuments = useMemo(
    () =>
      collectDocChatContextDocuments({
        activeTabId,
        conversation: docChat?.conversation,
        openTabs,
        pipelineConfigId: selectedPipelineConfigId,
        previewDocumentId: docChat?.previewDocumentId,
        previewPage: docChat?.previewPage,
      }),
    [
      activeTabId,
      docChat?.conversation,
      docChat?.previewDocumentId,
      docChat?.previewPage,
      openTabs,
      selectedPipelineConfigId,
    ]
  )
  const {
    rawDocumentSearchData,
    streamingSearchData,
    isLoadingSearch,
    searchEngine,
    searchLatencyMs,
    refreshDocumentSearch,
  } = useEnabledDocumentSearch({
    siteId,
    tabId,
    searchQuery,
    contentFilter,
    searchMode,
    linkedMode,
    browseAll,
    docChat,
    attachments: sentAttachments,
    contextDocuments: docChatContextDocuments,
  })
  const hydrationSearchData = rawDocumentSearchData ?? streamingSearchData

  const knowledgeDocLoadOptions = useMemo(() => {
    if (
      isPipelineConfigScopeLoading ||
      !unitId ||
      !siteId ||
      (linkedDocIds && linkedDocIds.length > 0)
    ) {
      return undefined
    }

    const scopedTags = getPipelineConfigScopedTags({
      site: siteId,
      unit: unitId,
    })
    if (!scopedTags) return undefined

    return {
      type: [RolodexEntryType.KNOWLEDGE_DOCUMENT],
      tags: scopedTags,
      limit: 1000000,
    }
  }, [
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    linkedDocIds,
    siteId,
    unitId,
  ])

  const { data: knowledgeDocsForUnit, isLoading: isLoadingUnit } =
    useGetRolodexEntriesByLoadOptions<KnowledgeDocumentEntry[]>(
      knowledgeDocLoadOptions
    )

  const unitSystemEntryIds = useMemo(() => {
    if (!knowledgeDocsForUnit) return []
    const ids = new Set<string>()
    for (const entry of knowledgeDocsForUnit) {
      const systemDocTag = entry.entry.tags['system-document']
      if (systemDocTag) ids.add(systemDocTag)
    }
    return Array.from(ids)
  }, [knowledgeDocsForUnit])

  const searchBqDocumentIds = useMemo(() => {
    if (linkedMode) return []
    if (browseAll) return []
    const ids = new Set<string>()
    for (const result of hydrationSearchData?.results ?? []) {
      ids.add(
        unscopeSearchDocumentIdForHydration(
          result.document_id,
          selectedPipelineConfigId
        )
      )
      for (const group of result.drawing_revision_groups ?? []) {
        for (const option of group.options) {
          ids.add(
            unscopeSearchDocumentIdForHydration(
              option.document_id,
              selectedPipelineConfigId
            )
          )
        }
      }
    }
    return Array.from(ids)
  }, [linkedMode, browseAll, hydrationSearchData, selectedPipelineConfigId])

  // Linked BQ ids, or unit-linked entry UUIDs — useSystemDocumentsByBqIds
  // partitions entry vs BQ ids so unit mode does not need a second Rolodex hop.
  const linkedHydrationIds = useMemo(() => {
    if (!linkedMode) return []
    if (linkedDocIds && linkedDocIds.length > 0) return linkedDocIds
    return unitSystemEntryIds
  }, [linkedMode, linkedDocIds, unitSystemEntryIds])

  const recommendedHydrationIds = linkedMode
    ? linkedHydrationIds
    : searchBqDocumentIds

  const conversationHydrationDocumentIds = useMemo(
    () =>
      collectConversationDocumentIds(
        docChat?.conversation,
        selectedPipelineConfigId
      ),
    [docChat?.conversation, selectedPipelineConfigId]
  )

  const hydrationDocumentIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...recommendedHydrationIds,
          ...conversationHydrationDocumentIds,
        ])
      ),
    [recommendedHydrationIds, conversationHydrationDocumentIds]
  )

  const { documents: searchedSystemDocuments, isLoading: isLoadingSystemDocs } =
    useSystemDocumentsByBqIds(hydrationDocumentIds)

  const recommendedDocumentIds = useMemo(() => {
    if (!linkedMode) return searchBqDocumentIds
    if (linkedDocIds && linkedDocIds.length > 0) return linkedDocIds

    const byEntryId = new Map(
      (searchedSystemDocuments ?? []).map((entry) => [entry.entry.id, entry])
    )
    const ids: string[] = []
    for (const entryId of unitSystemEntryIds) {
      const entry = byEntryId.get(entryId)
      const bqId = entry ? getSystemDocumentBqId(entry) : undefined
      if (bqId) ids.push(bqId)
    }
    return ids
  }, [
    linkedMode,
    linkedDocIds,
    searchBqDocumentIds,
    searchedSystemDocuments,
    unitSystemEntryIds,
  ])

  const baseIsLoadingDocuments = browseAll
    ? isPipelineConfigScopeLoading || isLoadingBrowsePage
    : linkedMode
      ? isLoadingSystemDocs ||
        (!!unitId &&
          (!linkedDocIds || linkedDocIds.length === 0) &&
          isLoadingUnit)
      : isLoadingSearch || isLoadingSystemDocs

  const {
    sensors: sensorResults,
    totalCount: sensorTotalCount,
    isLoading: isLoadingSensors,
  } = useSensorSearch({
    siteId,
    searchText: contentFilter === 'sensors' ? searchQuery : '',
    enabled: contentFilter === 'sensors',
  })

  const { recentItems, addRecentItem } = useRecentItems(siteId)

  const documentUserEditHydrationBqIds = useMemo(() => {
    const sourceEntries = browseAll
      ? (browsePageEntries ?? [])
      : (searchedSystemDocuments ?? [])
    const ids: string[] = []
    for (const entry of sourceEntries) {
      const bqId = getSystemDocumentBqId(entry)
      if (bqId) ids.push(bqId)
    }
    return ids
  }, [browseAll, browsePageEntries, searchedSystemDocuments])

  const elasticsearchHighlightsMap = useMemo(() => {
    if (linkedMode || browseAll) return new Map<string, Highlight[]>()
    const bySearchId = new Map<string, Highlight[]>()
    for (const result of hydrationSearchData?.results ?? []) {
      if (result.highlights && result.highlights.length > 0) {
        const cantileverDocumentId = unscopeSearchDocumentIdForHydration(
          result.document_id,
          selectedPipelineConfigId
        )
        bySearchId.set(
          cantileverDocumentId,
          result.highlights.map((highlight) => ({
            ...highlight,
            page_id:
              typeof highlight.page_id === 'string'
                ? unscopePageIdFromPipelineConfig(
                    highlight.page_id,
                    selectedPipelineConfigId
                  )
                : highlight.page_id,
          }))
        )
      }
    }
    const map = new Map<string, Highlight[]>()
    for (const entry of searchedSystemDocuments ?? []) {
      const bqId = getSystemDocumentBqId(entry)
      if (!bqId) {
        continue
      }
      const highlights = bySearchId.get(bqId)
      if (highlights) {
        map.set(bqId, highlights)
      }
    }
    return map
  }, [
    linkedMode,
    browseAll,
    hydrationSearchData,
    searchedSystemDocuments,
    selectedPipelineConfigId,
  ])

  const basePageClassificationsMap = useMemo(() => {
    if (linkedMode || browseAll || !isAiDocumentSearchEngine(searchEngine)) {
      return new Map<string, PageClassification[]>()
    }
    const bySearchId = new Map<string, PageClassification[]>()
    for (const result of hydrationSearchData?.results ?? []) {
      if (
        result.page_classifications &&
        result.page_classifications.length > 0
      ) {
        bySearchId.set(
          unscopeSearchDocumentIdForHydration(
            result.document_id,
            selectedPipelineConfigId
          ),
          result.page_classifications
        )
      }
    }
    return mapSearchValuesToDocumentRowIds(searchedSystemDocuments, bySearchId)
  }, [
    linkedMode,
    browseAll,
    searchEngine,
    hydrationSearchData,
    searchedSystemDocuments,
    selectedPipelineConfigId,
  ])

  const baseDrawingRevisionGroupsMap = useMemo(() => {
    if (linkedMode || browseAll || !isAiDocumentSearchEngine(searchEngine)) {
      return new Map<string, DrawingRevisionGroup[]>()
    }
    const bySearchId = new Map<string, DrawingRevisionGroup[]>()
    for (const result of hydrationSearchData?.results ?? []) {
      if (
        result.drawing_revision_groups &&
        result.drawing_revision_groups.length > 0
      ) {
        bySearchId.set(
          unscopeSearchDocumentIdForHydration(
            result.document_id,
            selectedPipelineConfigId
          ),
          result.drawing_revision_groups.map((group) => ({
            ...group,
            options: group.options.map((option) => ({
              ...option,
              document_id: unscopeSearchDocumentIdForHydration(
                option.document_id,
                selectedPipelineConfigId
              ),
            })),
          }))
        )
      }
    }
    return mapSearchValuesToDocumentRowIds(searchedSystemDocuments, bySearchId)
  }, [
    linkedMode,
    browseAll,
    searchEngine,
    hydrationSearchData,
    searchedSystemDocuments,
    selectedPipelineConfigId,
  ])

  const {
    pageClassificationsMap,
    drawingRevisionGroupsMap,
    documentUserEditsByBqId,
  } = useSearchUserEditHydration({
    pageEditsEnabled: shouldHydratePageUserEdits,
    documentEditsEnabled: shouldHydrateDocumentUserEdits,
    searchEngine,
    siteId,
    bqDocumentIds: documentUserEditHydrationBqIds,
    pageClassificationsMap: basePageClassificationsMap,
    drawingRevisionGroupsMap: baseDrawingRevisionGroupsMap,
  })

  const toHydratedTableRow = useMemo(() => {
    return (entry: SystemDocumentEntry): DocumentTableRow => {
      const bqId = getSystemDocumentBqId(entry)
      const editValues =
        bqId !== undefined ? documentUserEditsByBqId.get(bqId) : undefined
      return transformSystemDocumentToTableRow(entry, editValues)
    }
  }, [documentUserEditsByBqId])

  const documentResults = useMemo(() => {
    if (browseAll) {
      return (browsePageEntries ?? []).map((entry) => toHydratedTableRow(entry))
    }

    if (!searchedSystemDocuments) return []

    const rows = searchedSystemDocuments.map((entry) =>
      toHydratedTableRow(entry)
    )
    const topLevelSearchIds = hydrationSearchData?.results.map((result) =>
      unscopeSearchDocumentIdForHydration(
        result.document_id,
        selectedPipelineConfigId
      )
    )

    if (linkedMode) {
      return orderDocumentsById(rows, recommendedDocumentIds)
    }

    if (isAiDocumentSearchEngine(searchEngine)) {
      return orderDocumentsById(
        rows,
        topLevelSearchIds ?? recommendedDocumentIds,
        {
          filterToOrderedIds: true,
        }
      )
    }

    const scoresBySearchId = new Map<string, number>()
    for (const result of hydrationSearchData?.results ?? []) {
      scoresBySearchId.set(
        unscopeSearchDocumentIdForHydration(
          result.document_id,
          selectedPipelineConfigId
        ),
        result.score
      )
    }
    const scoresByRowId = new Map<string, number>()
    for (const row of rows) {
      const score = scoresBySearchId.get(row.id)
      if (score !== undefined) {
        scoresByRowId.set(row.id, score)
      }
    }
    return orderDocumentsByScoreDesc(rows, scoresByRowId)
  }, [
    browseAll,
    browsePageEntries,
    searchedSystemDocuments,
    hydrationSearchData,
    linkedMode,
    recommendedDocumentIds,
    searchEngine,
    selectedPipelineConfigId,
    toHydratedTableRow,
  ])
  const documentTotalCount = browseAll
    ? (browseTotalCount ?? 0)
    : documentResults.length
  const documentRowsById = useMemo(() => {
    const map = new Map<string, DocumentTableRow>()
    const sourceEntries = browseAll
      ? (browsePageEntries ?? [])
      : (searchedSystemDocuments ?? [])
    for (const entry of sourceEntries) {
      const row = toHydratedTableRow(entry)
      map.set(row.id, row)
    }
    return map
  }, [
    browseAll,
    browsePageEntries,
    searchedSystemDocuments,
    toHydratedTableRow,
  ])

  const citationDocumentLookup = useMemo(() => {
    if (!searchedSystemDocuments?.length) {
      return new Map<string, DocumentTableRow>()
    }
    return buildCitationDocumentLookup(
      searchedSystemDocuments.map((entry) => toHydratedTableRow(entry))
    )
  }, [searchedSystemDocuments, toHydratedTableRow])

  const isLoadingDocuments =
    browseAll || linkedMode
      ? baseIsLoadingDocuments
      : isLoadingSearch || (isLoadingSystemDocs && documentResults.length === 0)
  const refreshSearchOrBrowse = useCallback(() => {
    refreshDocumentSearch()
    void refreshBrowsePage()
  }, [refreshBrowsePage, refreshDocumentSearch])

  const browseAllDisplay = useMemo<BrowseAllDisplayState | undefined>(
    () =>
      browseAll
        ? {
            displayedPageIndex: browseDisplayedPageIndex,
            displayedSorting: browseDisplayedSorting,
            facetColumnValues: browseFacetColumnValues,
            loadFullFilteredSet: browseLoadFullFilteredSet,
          }
        : undefined,
    [
      browseAll,
      browseDisplayedPageIndex,
      browseDisplayedSorting,
      browseFacetColumnValues,
      browseLoadFullFilteredSet,
    ]
  )

  const contextValue = useMemo<DocumentSearchDataContextType>(
    () => ({
      browseAllDisplay,
      documentResults,
      documentTotalCount,
      isLoadingDocuments,
      documentsLoadError: browseAll ? browsePageError : undefined,
      sensorResults,
      sensorTotalCount,
      isLoadingSensors,
      recentItems,
      addRecentItem,
      elasticsearchHighlightsMap,
      pageClassificationsMap,
      drawingRevisionGroupsMap,
      documentRowsById,
      citationDocumentLookup,
      rawSearchData:
        linkedMode || browseAll ? undefined : rawDocumentSearchData,
      searchEngine,
      searchLatencyMs: linkedMode || browseAll ? undefined : searchLatencyMs,
      searchedSystemDocuments: browseAll
        ? browsePageEntries
        : searchedSystemDocuments,
      refreshDocumentSearch: refreshSearchOrBrowse,
    }),
    [
      browseAllDisplay,
      documentResults,
      documentTotalCount,
      isLoadingDocuments,
      sensorResults,
      sensorTotalCount,
      isLoadingSensors,
      recentItems,
      addRecentItem,
      elasticsearchHighlightsMap,
      pageClassificationsMap,
      drawingRevisionGroupsMap,
      documentRowsById,
      citationDocumentLookup,
      linkedMode,
      browseAll,
      browsePageError,
      rawDocumentSearchData,
      searchEngine,
      searchLatencyMs,
      browsePageEntries,
      searchedSystemDocuments,
      refreshSearchOrBrowse,
    ]
  )

  return (
    <DocumentSearchDataContext.Provider value={contextValue}>
      {children}
    </DocumentSearchDataContext.Provider>
  )
}

export const useDocumentSearchData = (): DocumentSearchDataContextType => {
  const context = useContext(DocumentSearchDataContext)
  if (context === undefined) {
    throw new Error(
      'useDocumentSearchData must be used within a DocumentSearchDataProvider'
    )
  }
  return context
}
